import { useEffect, useState } from "react";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { OrganisationService } from "@/services/Orgnisation/OrgnisationService";
import { DepartmentService } from "@/services/Department/Department";
import {
  Users,
  Building2,
  Network,
  Search,
  Sparkles,
  UserCheck,
  Maximize2,
  Minimize2,
  Award,
  Layers,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { Button } from "@/components/ui/button";
import { ThrowError } from "@/api/callApi";
import { GetFilterUrl } from "@/utility/helper";
import SearchSelect from "@/components/common/SearchSelect";
import {
  // EmployeeNode,
  OrgTreeNode,
  countTotalEmployees,
  countDepartments,
} from "./OrgTreeNode";
import type { EmployeeNode } from "./OrgTreeNode";
import type { department } from "@/interface/department.interface";
import FullPageLoader from "@/components/common/FullPageLoader";
import { useNavigate } from "react-router-dom";

const Organisation = () => {
  const navigate = useNavigate();
  const [rootNode, setRootNode] = useState<EmployeeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [defaultExpandDepth, setDefaultExpandDepth] = useState(2);
  const { user: userDetails } = useSelector((state: RootState) => state.user);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(
    () => "",
  );
  // const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(
  //   () => userDetails?.department?.id || "",
  // );
  const [departments, setDepartments] = useState<{ value: string; label: string }[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformNode = (apiNode: any): EmployeeNode => ({
    id: apiNode.id,
    name: apiNode.name,
    role: apiNode.designationName || "—",
    department: apiNode.departmentName || "—",
    employeeCode: apiNode.employeeCode || "—",
    reports: apiNode.children?.map(transformNode) || [],
  });

  // Fetch all departments for dropdown selector
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await DepartmentService.getDepartment();
        if (response?.status && response?.data?.department) {
          const options = response?.data?.department?.map((dept: department) => ({
            value: dept.id || "",
            label: dept.name || "",
          })) || [];
          setDepartments(options);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
      }
    };
    fetchDepartments();
  }, []);

  // Sync selectedDepartmentId once if userDetails arrives after mount
  // useEffect(() => {
  //   if (userDetails?.department?.id && !selectedDepartmentId) {
  //     setSelectedDepartmentId(userDetails.department.id);
  //   }
  // }, [userDetails?.department?.id, selectedDepartmentId]);

  // Fetch Organisation structure hierarchy on mount and when filters change
  useEffect(() => {
    if (!userDetails?.id) return;

    const fetchData = async () => {
      // const activeDeptId = selectedDepartmentId || userDetails?.department?.id || "";

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const query: any = {
        employeeId: userDetails.id,
      };
      // if (activeDeptId) {
      //   query.departmentId = activeDeptId;
      // }
      if (selectedDepartmentId) {
        query.departmentId = selectedDepartmentId;
      }
      const queryUrl = GetFilterUrl(query);

      try {
        setLoading(true);
        const response = await OrganisationService.getList(queryUrl);
        const roots = response?.data?.roots || [];

        if (roots.length === 0) {
          setRootNode(null);
          setError("No organisation data found.");
          return;
        }

        const syntheticRoot: EmployeeNode = {
          id: "synthetic-root",
          name: "Organization",
          role: "",
          department: "",
          employeeCode: "",
          reports: roots.map(transformNode),
        };

        setRootNode(syntheticRoot);
        setError(null);
      } catch (err) {
        setRootNode(null);
        setError("Could not load organisation structure. Please try again.");
        ThrowError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDepartmentId, userDetails?.id]);

  if (loading) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    );
  }

  const totalEmployees = rootNode ? countTotalEmployees(rootNode) : 0;
  const totalDepartments = rootNode ? countDepartments(rootNode) : 0;

  const departmentOptions = [
    { value: "", label: "All Departments" },
    ...departments
  ];

  return (
    <Layout>
      <div className="space-y-5 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <MainHeading title="Organisation Structure" />
          <div className="flex items-center gap-4 self-end sm:self-auto">
            {/* Department Search Selector */}
            <div className="w-56 text-left">
              <SearchSelect
                placeholder="Filter by Department..."
                options={departmentOptions}
                value={selectedDepartmentId}
                onChange={(val) => setSelectedDepartmentId(val)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDefaultExpandDepth(defaultExpandDepth >= 50 ? 1 : 50)}
              className="text-xs flex items-center gap-1.5 font-semibold shadow-2xs border-slate-200/80 hover:bg-slate-50 px-3 py-1.5 rounded-lg h-9"
            >
              {defaultExpandDepth >= 50 ? (
                <>
                  <Minimize2 className="w-3.5 h-3.5 text-primary" /> Collapse All
                </>
              ) : (
                <>
                  <Maximize2 className="w-3.5 h-3.5 text-primary" /> Expand All
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Top Overview Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs hover:shadow-sm transition-all duration-300 group">
            <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity text-primary transform group-hover:scale-110 duration-300">
              <Users size={64} />
            </div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total Workforce</p>
                <h3 className="text-xl font-bold text-slate-800 mt-0.5">{totalEmployees}</h3>
                <p className="text-[11px] text-emerald-600 mt-1 font-medium flex items-center gap-1">
                  <UserCheck className="w-3 h-3" /> Active Personnel
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-primary text-white flex items-center justify-center shadow-md shadow-primary/15">
                <Users className="w-5 h-5" />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs hover:shadow-sm transition-all duration-300 group">
            <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity text-purple-600 transform group-hover:scale-110 duration-300">
              <Building2 size={64} />
            </div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Departments</p>
                <h3 className="text-xl font-bold text-slate-800 mt-0.5">{totalDepartments}</h3>
                <p className="text-[11px] text-purple-600 mt-1 font-medium flex items-center gap-1">
                  <Layers className="w-3 h-3" /> Functional Units
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-purple-500/15">
                <Building2 className="w-5 h-5" />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-xl p-4 shadow-2xs hover:shadow-sm transition-all duration-300 group">
            <div className="absolute top-0 right-0 p-3 opacity-5 group-hover:opacity-10 transition-opacity text-emerald-600 transform group-hover:scale-110 duration-300">
              <Network size={64} />
            </div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Logged In Role</p>
                <h3 className="text-sm font-bold text-slate-800 mt-0.5 truncate max-w-[170px]">
                  {userDetails?.designation?.name || userDetails?.designationName || "Staff Member"}
                </h3>
                <p className="text-[11px] text-emerald-600 mt-1 font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Active Profile
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/15">
                <Award className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar and Tree Card */}
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-2xs overflow-hidden">
          {/* Header Bar with Search */}
          <div className="py-3 px-4 border-b border-slate-100 bg-slate-50/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center text-primary shadow-inner shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                {rootNode && (
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold tracking-tight">{rootNode.name}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 border border-emerald-500/30 font-semibold">
                        Active Structure
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs font-bold text-primary">
                        {rootNode.reports?.length} {rootNode?.reports?.length === 1 ? "Department" : "Departments"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search name, role, dept or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-11 py-1.5 text-xs bg-white border border-slate-200/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-2xs placeholder:text-slate-400 h-8"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 hover:text-slate-600 font-semibold bg-slate-100 px-1.5 py-0.5 rounded"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Org Tree View */}
          <div className="p-4 overflow-auto bg-slate-50/20">
            {error ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                {error}
              </div>
            ) : rootNode && rootNode.reports && rootNode.reports.length > 0 ? (
              <div className="space-y-3">
                {/* Root Children */}
                <div className="space-y-2 pl-1">
                  {rootNode.reports.map((report) => (
                    <OrgTreeNode
                      key={report.id}
                      node={report}
                      isRoot={true}
                      depth={0}
                      userDetails={userDetails}
                      defaultExpandDepth={defaultExpandDepth}
                      searchQuery={searchQuery}
                      navigate={navigate}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500 text-sm">
                No reporting structure found under this organization.
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Organisation;